import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import SuggestedActionsTable from './components/SuggestedActionsTable';
import {
  MULTI_SORT_VALUES,
  SuggestedActionsTableData,
} from './components/SuggestedActionsTable/data';
import useQueryParams from 'hooks/useQueryParams';
import api from 'api';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import MultiFilter from 'components/MultiFilter';
import MultiSort from 'components/MultiSort';
import { useTranslation } from 'react-i18next';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useGetSearchableFilterOptions from '../../hooks';
import {
  DEAL_FILTER_KEY,
  DEAL_TITLE_FILTER_KEY,
  TIMEFRAME_FILTER,
  USER_ID_FILTER_KEY,
  USER_NAME_FILTER_KEY,
} from '../../types';
import { FilterOption } from 'models/Filters';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';

import './SuggestedActions.styles.scss';
type SuggestedActionsProps = {
  className?: string;
};

const SuggestedActions: React.FC<SuggestedActionsProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<SuggestedActionsTableData[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  const { limit, updateLimit } = useNumberOfResultsPerPage();
  const {
    params: { page, sort, userId, dealRoomIds, timeframe },
  } = useQueryParams();

  const classes = classNames('suggested-actions', className);

  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();
  const { getDealOptions, getAllUserOptions } = useGetSearchableFilterOptions();

  const ownerFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('owner'),
        USER_ID_FILTER_KEY,
        USER_NAME_FILTER_KEY,
        getAllUserOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getAllUserOptions, t],
  );

  const dealFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('deal'),
        DEAL_FILTER_KEY,
        DEAL_TITLE_FILTER_KEY,
        getDealOptions,
      ) as FilterOption,
    [getDealOptions, getMultiSelectSearchableFilter, t],
  );

  const timeFilterOption = useMemo(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('lastActivity'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME_FILTER)),
    }),
    [createSelectOptions, t],
  );

  const filterOptions = useMemo(
    () => [ownerFilterOption, dealFilterOption, timeFilterOption],
    [dealFilterOption, ownerFilterOption, timeFilterOption],
  );

  const translatedSortOptions = useMemo(
    () =>
      MULTI_SORT_VALUES.map(({ value, label }) => ({
        value,
        label: t(label),
      })),
    [t],
  );

  const multiSortDirectionTranslation = useMemo<SortingDirectionValues>(
    () => ({
      lastActivity: {
        [SORTING_ORDER.A_TO_Z]: t('oldestFirst'),
        [SORTING_ORDER.Z_TO_A]: t('recentFirst'),
      },
    }),
    [t],
  );

  const columnFilterOptions = useMemo(
    () => ({
      dealName: {
        options: getMultiSelectSearchableFilter(
          t('deal'),
          DEAL_FILTER_KEY,
          DEAL_TITLE_FILTER_KEY,
          getDealOptions,
        ) as FilterOption,
      },
      owner: {
        options: getMultiSelectSearchableFilter(
          t('owner'),
          USER_ID_FILTER_KEY,
          USER_NAME_FILTER_KEY,
          getAllUserOptions,
        ) as FilterOption,
      },
      lastUpdated: {
        options: timeFilterOption,
        sortDirectionTranslation: multiSortDirectionTranslation.lastActivity,
      },
    }),
    [
      multiSortDirectionTranslation,
      timeFilterOption,
      getDealOptions,
      getAllUserOptions,
      getMultiSelectSearchableFilter,
      t,
    ],
  ) as Record<string, ColumnFilterOption>;

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getBossModeSuggestedActions({
          $page: page,
          $limit: limit,
          $sort: sort,
          userId,
          dealRoomIds,
          timeframe,
        });
        setItems(items);
        setTotalPages(totalPages);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, limit, sort, userId, dealRoomIds, timeframe]);

  return (
    <div className={classes}>
      <div>
        <ActiveFilterSortPills
          filterOptions={filterOptions}
          sortOptions={translatedSortOptions}
        />

        <div className="filter-wrapper">
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
          <MultiFilter filterOptions={filterOptions} />
          <MultiSort
            multiSortValues={translatedSortOptions}
            multiSortDirectionTranslation={multiSortDirectionTranslation}
          />
        </div>
      </div>

      <SuggestedActionsTable
        isLoading={isLoading}
        data={items}
        totalPages={totalPages}
        columnFilterOptions={columnFilterOptions}
      />
    </div>
  );
};

export default SuggestedActions;
